import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, Server, Database, Palette } from 'lucide-react';
import { AnimatedSection } from '../ui/AnimatedSection';
import { skills } from '../../data/portfolioData';
import { Category } from '../../types';

const categories: Category[] = [
  { id: 'frontend', label: 'Frontend', icon: <Globe /> },
  { id: 'backend', label: 'Backend', icon: <Server /> },
  { id: 'database', label: 'Database', icon: <Database /> },
  { id: 'design', label: 'Design', icon: <Palette /> },
];

interface SkillsProps {
  onSkillClick?: (skillName: string) => void;
}

export const Skills: React.FC<SkillsProps> = ({ onSkillClick }) => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const filteredSkills = selectedCategory 
    ? skills.filter(skill => skill.category === selectedCategory)
    : skills;

  const handleSkillClick = (skillName: string) => {
    if (onSkillClick) {
      onSkillClick(skillName);
      // Scroll to projects section
      document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="skills" className="py-20 bg-slate-50 dark:bg-slate-900 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0">
        <div className="absolute top-16 left-1/4 w-1 h-1 bg-indigo-400 rounded-full opacity-20 animate-float-dots" />
        <div className="absolute top-2/3 right-10 w-1.5 h-1.5 bg-primary-400 rounded-full opacity-15 animate-float-dots" 
             style={{ animationDelay: '-4s' }} />
        <div className="absolute bottom-1/4 left-10 w-2 h-2 bg-indigo-500 rounded-full opacity-10 animate-float-dots" 
             style={{ animationDelay: '-7s' }} />
        
        {/* Floating lines */}
        <div className="absolute top-1/3 right-0 h-px w-40 bg-gradient-to-l from-transparent via-indigo-400 to-transparent opacity-10 animate-float-lines" />
        <div className="absolute bottom-1/2 left-0 h-px w-32 bg-gradient-to-r from-transparent via-primary-400 to-transparent opacity-8 animate-float-lines" 
             style={{ animationDelay: '-5s' }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <AnimatedSection>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-slate-100 mb-4">
              Compétences
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-indigo-500 to-primary-500 mx-auto mb-8"></div>
            
            {/* Category filters */}
            <div className="flex flex-wrap justify-center gap-4">
              <motion.button
                onClick={() => setSelectedCategory(null)}
                className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center gap-2 border ${
                  selectedCategory === null
                    ? 'bg-gradient-to-r from-indigo-600 to-primary-600 text-white shadow-lg border-indigo-500/30'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-slate-700 border-slate-200 dark:border-slate-700'
                }`}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                Toutes
              </motion.button>
              
              {categories.map((category) => (
                <motion.button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center gap-2 border ${
                    selectedCategory === category.id
                      ? 'bg-gradient-to-r from-indigo-600 to-primary-600 text-white shadow-lg border-indigo-500/30'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-slate-700 border-slate-200 dark:border-slate-700'
                  }`}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {category.icon}
                  {category.label}
                </motion.button>
              ))}
            </div>
          </div>
        </AnimatedSection>

        {/* Skills grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedCategory}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredSkills.map((skill, index) => (
              <motion.div
                key={skill.name}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-slate-800 rounded-2xl p-6 hover:shadow-lg transition-all duration-300 cursor-pointer group border border-slate-200 dark:border-slate-700 relative overflow-hidden"
                whileHover={{ y: -5, scale: 1.02 }}
                onClick={() => handleSkillClick(skill.name)}
              >
                {/* Subtle background gradient on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-primary-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-4">
                    <motion.div 
                      className="p-3 bg-gradient-to-r from-indigo-600 to-primary-600 rounded-lg text-white group-hover:shadow-lg transition-shadow"
                      whileHover={{ scale: 1.1, rotate: 5 }}
                    >
                      {skill.icon}
                    </motion.div>
                    <div>
                      <h3 className="font-semibold text-slate-900 dark:text-slate-100 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                        {skill.name}
                      </h3>
                      <div className="text-sm text-slate-500 dark:text-slate-400">
                        {skill.level}% maîtrisé
                      </div>
                    </div>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="relative mb-4">
                    <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3 overflow-hidden">
                      <motion.div
                        className="bg-gradient-to-r from-indigo-500 to-primary-500 h-3 rounded-full relative"
                        initial={{ width: 0 }}
                        whileInView={{ width: `${skill.level}%` }}
                        transition={{ delay: index * 0.1 + 0.5, duration: 1, ease: "easeOut" }}
                        viewport={{ once: true }}
                      >
                        {/* Shine effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                      </motion.div>
                    </div>
                    <motion.div 
                      className="absolute -top-8 right-0 text-xs text-slate-500 dark:text-slate-400 font-medium bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded-md"
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1 + 1.2 }}
                      viewport={{ once: true }}
                    >
                      {skill.level}%
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};